// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// Where the things a player touches actually are (M5-05, M5-08, M5-13).
//
// Everything here is in **cabinet-local metres**, with the origin on the floor at the centre of
// the cabinet's footprint, +X to the player's right, +Y up and **+Z toward the player**. That
// last one is worth stating because Three.js's camera looks down −Z, so "toward the player" and
// "toward the camera" are the same direction and the front of the cabinet is at `+length/2`.
//
// **Derived from the cabinet, never typed beside it.** Every position is computed from a
// `CabinetDimensions`, so a machine that states a narrower body gets its buttons on the narrower
// body. This project has already been bitten twice by a measurement typed next to the thing it
// describes — the calibration rig's bat pose, and the flipper tunings — and a flipper button
// 20 mm inside the side panel is the version of that bug a player feels rather than reads.
//
// **The whole layout scales.** In MR tabletop mode the cabinet is 0.42 scale (06 §3), which is
// a world transform rather than a different machine: the layout is computed once in cabinet
// space and the transform does the rest, so a tabletop button is exactly where the full-size one
// is, 0.42 of the way there.

import type { CabinetDimensions } from '../world/cabinet'
import { BALL_RADIUS } from '@core/sim/constants'

/** A point in cabinet-local metres. */
export interface ControlPoint {
  readonly x: number
  readonly y: number
  readonly z: number
}

/**
 * A pressable control: a point, and the capsule around it that counts as touching it.
 *
 * `radius` is the generous part and `travel` is the firm part, which together are XR-004's
 * second latency mitigation. A fingertip anywhere inside the capsule is *at* the button; it has
 * to move inward past `pressAt` to press it.
 */
export interface PressableControl {
  readonly id: PressableId
  readonly at: ControlPoint
  /** Radius of the activation capsule, metres. */
  readonly radius: number
  /** Inward direction the press is measured along, unit length in cabinet space. */
  readonly inward: ControlPoint
}

export type PressableId = 'flipperLeft' | 'flipperRight' | 'start' | 'coin'

/**
 * Every pressable on the cabinet, in the order the gates are held.
 *
 * **One list**, the twin of C#'s `CabinetControls.Pressables`. `cabinetRig` and `cabinetGrab` each
 * kept their own copy until M8-30, and when the bar-top flipper buttons were added and taken away
 * again both had to be edited both times — which is exactly the shape of thing one of them
 * eventually gets wrong.
 */
export const PRESSABLE_IDS: readonly PressableId[] = [
  'flipperLeft',
  'flipperRight',
  'start',
  'coin',
]

/**
 * Activation capsule radius, metres.
 *
 * 06 §2.1 says 0.030 m and says why: "missing a flip is unforgivable and no player will ever
 * complain that a button was too easy to hit". A real flipper button is about 0.028 m across, so
 * this is roughly twice the physical target in every direction — deliberately, because the thing
 * being aimed at is invisible to the tracking system, not to the player.
 */
export const BUTTON_RADIUS = 0.030

/**
 * How far off a button's axis a fingertip may be and still press it, metres (M8-28).
 *
 * **Bigger than the cap the player sees, deliberately.** The split is what this file's own header
 * has always described — "`radius` is the generous part and `travel` is the firm part" — and the
 * two were one number, so the generosity it promised did not exist: the reach stopped at the drawn
 * edge. A real flipper button is about 28 mm across and this one is drawn at 60 mm, so the answer
 * to a player missing it is not to keep drawing it bigger; it is to let the target be larger than
 * the picture, which is what every 2-D interface does and nobody notices.
 *
 * **65 mm, widened from 45 on 31 Aug 2026 (M8-30).** The Quest build at 45 mm measured 142 flipper
 * presses at a median hold of 90 ms with 5% under one frame, against 274 at 41 ms with 24% under
 * one frame on the old sphere — so the geometry was right and the remaining ask was forgiveness.
 * Bounded by the layout rather than by taste: `start` and the left flipper button are the nearest
 * pair at 0.25 m centre to centre, so two capsules still cannot hold one finger.
 */
export const BUTTON_REACH = 0.065

/**
 * How far off a *flipper* button's axis a fingertip may be and still press it, metres.
 *
 * **80 mm, widened from `BUTTON_REACH`'s 65 on 14 Sep 2026**, at the product owner's ask after the
 * round the start button reached the front door: "the flipper buttons should be twice their
 * radius because the user cannot see the flippers so they have to guess position. if the flipper
 * buttons were larger then the flippers would be more tolerant". Two answers, both here: the
 * zone is this, and the cap is drawn at `FLIPPER_CAP_RADIUS`, near enough the zone that the
 * picture is the target; and a lamp on the rail above each button says when a fingertip is in
 * the zone (`controlPicture.ts`), which is what a player looking at the ball cannot see. Still
 * bounded by the layout — the start button's centre is 0.247 m from the left flipper's — and the
 * firm part is unchanged: 8 mm of inward travel presses, so a palm resting on the rail does not.
 */
export const FLIPPER_REACH = 0.08

/** The flipper caps as drawn, metres. Twice `BUTTON_RADIUS`: the zone's size, near enough. */
export const FLIPPER_CAP_RADIUS = 0.06

/**
 * How far in front of a cap's face a fingertip counts as *over* the button, metres — the rail
 * lamp's green. Inside the zone's radius, and no further out than this: a hand hanging half a
 * metre in front of the flank is not over anything.
 */
export const HOVER_REACH = 0.05

/**
 * How far past a button a press may reach and still be a press, metres (M8-28).
 *
 * **A virtual button has no stop**, so this is the only thing that ends the press volume on the
 * inward side, and it wants to be generous rather than tight. The old bound was the activation
 * sphere itself at 30 mm, which a fingertip cleared without noticing — see `pressGate`.
 */
export const PRESS_DEPTH = 0.120

/** Press at 8 mm of inward travel, release at 4 mm (06 §2.1). The gap is the anti-chatter. */
export const PRESS_TRAVEL = 0.008
export const RELEASE_TRAVEL = 0.004

/** Plunger travel, metres (06 §2.2). */
export const PLUNGER_TRAVEL = 0.055

/**
 * The plunger's grab zone: a capsule along the rod's outer end (14 Sep 2026).
 *
 * It was a 45 mm sphere at the knob's centre, tested against the *fingertip*, and a pinch was the
 * only grab. A real hand wrapping a knob does neither: the index tip curls round the knob, four
 * or five centimetres from its centre, and a wrapped hand is a fist, not a thumb-to-index pinch.
 * "it would be nice if the grab of the plunger was more tolerant on location of hand so it
 * responds when user's hand is grabbing but not exactly on plunger ball location". So the zone is
 * the rod's outer end — `PLUNGER_GRAB_ACROSS` off the axis, from `PLUNGER_GRAB_AHEAD` beyond the
 * knob back to `PLUNGER_GRAB_BEHIND` toward the cabinet — and the fingertip, the pinch point and
 * the palm are all asked (`inPlungerZone`); a closed hand grabs as well as a pinch, provided its
 * palm is within `PLUNGER_GRAB_WRAP` of the axis, which is where a hand wrapped round a knob has
 * it — a fist merely passing the knob does not.
 */
export const PLUNGER_GRAB_ACROSS = 0.07
export const PLUNGER_GRAB_AHEAD = 0.04
export const PLUNGER_GRAB_BEHIND = 0.09
export const PLUNGER_GRAB_WRAP = 0.045

/**
 * Is a point in the plunger's grab zone — the capsule along the rod's outer end?
 *
 * `across` narrows the zone for a point that has to be *on* the rod (a closed hand's palm).
 */
export function inPlungerZone(
  plunger: { readonly at: ControlPoint; readonly axis: ControlPoint },
  x: number,
  y: number,
  z: number,
  across = PLUNGER_GRAB_ACROSS,
): boolean {
  const dx = x - plunger.at.x
  const dy = y - plunger.at.y
  const dz = z - plunger.at.z
  const along = dx * plunger.axis.x + dy * plunger.axis.y + dz * plunger.axis.z
  if (along > PLUNGER_GRAB_AHEAD || along < -PLUNGER_GRAB_BEHIND) return false
  const off2 = dx * dx + dy * dy + dz * dz - along * along
  return off2 <= across * across
}

/** How far the knob stands out beyond the cabinet's front face, metres along the cabinet's z. */
export const PLUNGER_KNOB_OUT = 0.055

/** The rubber tip on the end of the rod, metres. It rests against the ball. */
export const PLUNGER_TIP_RADIUS = 0.0105

/**
 * How far back from the front of the cabinet the flipper buttons sit, metres.
 *
 * 06 §2.1: "approximately 0.14 m back from the front of the cabinet and 0.05 m below the
 * lockdown bar". Both are real-cabinet measurements and both matter for the same reason — a
 * player resting their palms on the side rails must find the buttons under their fingers without
 * looking, because they will be looking at the ball.
 */
export const BUTTON_SET_BACK = 0.14
export const BUTTON_BELOW_BAR = 0.05

/** How far below the lockdown bar the start button sits on the front door, metres. */
export const START_BELOW_BAR = 0.08

/** The whole set of things a player can touch on this cabinet. */
export interface CabinetControls {
  readonly flipperLeft: PressableControl
  readonly flipperRight: PressableControl
  readonly start: PressableControl
  readonly coin: PressableControl
  /** The plunger knob at rest, and the axis it is pulled along (unit, pointing at the player). */
  /**
   * The shooter rod: the knob at rest, the axis it is pulled along, and the tip that rests
   * against the ball. **One rod, along the lane** (14 Sep 2026: "pinball plunger looks odd,
   * doesn't line up properly with the inside plunger and outside plunger"). It was two pictures
   * — the playfield drew a stub in the lane along the incline, the cabinet drew a level rod
   * through the front at a height of its own — and they met nowhere. A real shooter is mounted at
   * the playfield's angle so it strikes the ball square, so the axis is the lane's: tilted down
   * toward the player by the incline, through the ball's resting centre. The tip sits a ball and
   * a tip behind that centre, the knob `PLUNGER_KNOB_OUT` beyond the front face along the same
   * line — a little lower than the ball, which is what a real knob is.
   */
  readonly plunger: { readonly at: ControlPoint; readonly axis: ControlPoint; readonly tip: ControlPoint }
  /** Half-extents of the cabinet body, for the nudge contact test. */
  readonly body: { readonly halfWidth: number; readonly halfLength: number; readonly top: number }
}

/**
 * Lay the controls out on a given body.
 *
 * The buttons are on the **outside** of the side panels, which is where a real one is: the
 * button barrel goes through the panel and the player's finger never enters the cabinet. Putting
 * them on the inside face would work for the maths and read, in passthrough, as reaching *into*
 * the machine.
 */
export function cabinetControlsFor(dims: CabinetDimensions): CabinetControls {
  const halfWidth = dims.width / 2
  const halfLength = dims.length / 2
  const frontZ = halfLength
  const buttonZ = frontZ - BUTTON_SET_BACK
  const buttonY = dims.lockdownBarHeight - BUTTON_BELOW_BAR
  const plunger = shooterRod(dims, halfWidth, frontZ)
  const startX = -0.16, frontControlY = dims.lockdownBarHeight - START_BELOW_BAR

  const side = (sign: 1 | -1, id: PressableId): PressableControl => ({
    id,
    at: { x: sign * halfWidth, y: buttonY, z: buttonZ },
    radius: FLIPPER_REACH,
    // Pressed inward, so the press direction is toward the cabinet's centre line.
    inward: { x: -sign, y: 0, z: 0 },
  })

  return {
    flipperLeft: side(-1, 'flipperLeft'),
    flipperRight: side(1, 'flipperRight'),
    // **On the front door, left of centre, below the bar, pressed into the cabinet** — where a
    // real one is (14 Sep 2026). It was "on the lockdown bar, pressed downward", at the bar's own
    // height and 20 mm behind the front: the exact centre of the bar's 35 × 55 mm box, so the
    // 24 mm cap `cabinet.ts` draws there was inside the bar and never seen — "credit/start button
    // does not show (always been wrong, buried in machine edge)". A press gate at that point still
    // fired, which is why the fault survived: nobody could find the button to say it was missing.
    start: {
      id: 'start',
      at: { x: startX, y: frontControlY, z: frontZ },
      radius: BUTTON_REACH,
      inward: { x: 0, y: 0, z: -1 },
    },
    // CREDIT shares START's supported front-panel height, between START and the shooter.
    // The old 24 cm drop put its entire cap below the manufactured cabinet body.
    coin: {
      id: 'coin',
      at: { x: (startX + plunger.at.x) / 2, y: frontControlY, z: frontZ },
      radius: BUTTON_REACH,
      inward: { x: 0, y: 0, z: -1 },
    },
    plunger,
    body: { halfWidth, halfLength, top: dims.lockdownBarHeight },
  }
}

/**
 * The shooter rod for this cabinet: along the lane when the machine states one, level through the
 * front at the old height when it does not (a definition with no plunger still gets a rod; the
 * cabinet has a hole in the front either way).
 *
 * `lockdownBarHeight − 0.045` put the rod at 0.869 m on NEON NOVA while the ball it launches rests
 * at 0.914 — 45 mm under the lane, on every machine, and level where the lane is tilted (14 Sep
 * 2026: "Plunger does not line up with plunger shaft in pinball machine"). `shooterX/Y/Z` are the
 * ball's resting centre in cabinet metres, derived from the playfield the same way `cabinet.ts`
 * places it.
 */
function shooterRod(
  dims: CabinetDimensions,
  halfWidth: number,
  frontZ: number,
): { readonly at: ControlPoint; readonly axis: ControlPoint; readonly tip: ControlPoint } {
  const knobZ = frontZ + PLUNGER_KNOB_OUT
  if (dims.shooterX === undefined || dims.shooterY === undefined || dims.shooterZ === undefined) {
    const x = dims.shooterX ?? halfWidth - 0.075
    const y = dims.lockdownBarHeight - 0.045
    return {
      at: { x, y, z: knobZ },
      axis: { x: 0, y: 0, z: 1 },
      // Where the old rod was buried to: `PLUNGER_ROD_BURY` behind the front face.
      tip: { x, y, z: frontZ - 0.075 },
    }
  }
  const ball = { x: dims.shooterX, y: dims.shooterY, z: dims.shooterZ }
  // Down the lane toward the player: the incline, pointing forward and a little down.
  const axis = { x: 0, y: -Math.sin(dims.incline), z: Math.cos(dims.incline) }
  // The knob keeps its stand-off beyond the front face along z; along the tilted axis that is a
  // little further, and a little lower.
  const toKnob = (knobZ - ball.z) / axis.z
  const toTip = BALL_RADIUS + PLUNGER_TIP_RADIUS
  return {
    at: { x: ball.x, y: ball.y + axis.y * toKnob, z: knobZ },
    axis,
    tip: { x: ball.x, y: ball.y + axis.y * toTip, z: ball.z + axis.z * toTip },
  }
}

/*
 * --- The shooter's spring, pull gauge and knob (26 Sep 2026, owner: "plunger/shooter polish —
 * all three, in that order") --------------------------------------------------------------------
 *
 * Pure: where the parts are and what they show for a given pull. `cabinet.ts` ↔ `Cabinet.cs` draw
 * them and `controlPicture.ts` ↔ `ControlPicture.cs` move and light them; this file and
 * `CabinetControls.cs` hold the numbers, constant for constant.
 */

/**
 * The outer spring: a chrome coil on the rod between the front panel and the knob, where a real
 * machine's is and where a player sees it (the owner chose it over the inner spring, which the Blender
 * render showed hidden behind the panel). Anchored at the panel's outer face; its far end sits
 * behind the knob, so a pull stretches it by the draw.
 */
export const PLUNGER_SPRING_COILS = 5
/** The wire's radius and the coil's (to the wire's centre), metres. It clears the 4.5 mm rod. */
export const PLUNGER_SPRING_WIRE = 0.0011
export const PLUNGER_SPRING_RADIUS = 0.0085
/** How far short of the knob's centre the spring ends, metres: the 19 mm knob and a millimetre. */
export const PLUNGER_SPRING_KNOB_GAP = 0.02
/** Path points per turn of the drawn coil. */
export const PLUNGER_SPRING_STEPS = 16

/**
 * The spring on the shooter rod: fixed at the front panel's outer face, running out along the rod
 * `rest` metres to the back of the knob. `direction` points from the anchor out of the cabinet,
 * toward the player, which is the way the knob goes when the rod is pulled.
 */
export interface PlungerSpring {
  readonly anchor: ControlPoint
  readonly direction: ControlPoint
  readonly rest: number
}

/**
 * The spring for this rod, or nothing when there is no room between the panel and the knob. The
 * panel's outer face is `PLUNGER_KNOB_OUT` back from the knob along z, which along the tilted rod is
 * a little further.
 */
export function plungerSpringOf(plunger: CabinetControls['plunger']): PlungerSpring | undefined {
  const { at, axis } = plunger
  if (!(axis.z > 0)) return undefined
  const toFace = PLUNGER_KNOB_OUT / axis.z
  const rest = toFace - PLUNGER_SPRING_KNOB_GAP
  if (!(rest > 0.01)) return undefined
  return {
    anchor: { x: at.x - axis.x * toFace, y: at.y - axis.y * toFace, z: at.z - axis.z * toFace },
    direction: { x: axis.x, y: axis.y, z: axis.z },
    rest,
  }
}

/** The spring's length with the rod drawn `drawn` metres: longer by the pull, never shorter than at rest. */
export function plungerSpringLength(rest: number, drawn: number): number {
  return rest + Math.max(0, drawn)
}

/**
 * The coil's centre line at its rest length, in the spring's own frame: the spring runs up +y from
 * the anchor at the origin, and turns about it in x and z. Both pictures sweep a tube along this
 * and stand it on `direction`; the pull then scales that frame's y by `length / rest`.
 */
export function plungerSpringPath(rest: number): ControlPoint[] {
  const count = PLUNGER_SPRING_COILS * PLUNGER_SPRING_STEPS
  const points: ControlPoint[] = []
  for (let i = 0; i <= count; i++) {
    const turn = (i / PLUNGER_SPRING_STEPS) * 2 * Math.PI
    points.push({
      x: PLUNGER_SPRING_RADIUS * Math.cos(turn),
      y: (rest * i) / count,
      z: PLUNGER_SPRING_RADIUS * Math.sin(turn),
    })
  }
  return points
}

/**
 * The pull gauge: a row of lamps on the front panel under the rod, lit from the left as the rod is
 * drawn, so a player can see how hard they are about to plunge — and a marker left on the last
 * shot's lamp so a skill shot can be repeated.
 */
export const PULL_GAUGE_SEGMENTS = 8
/** Centre to centre along the row, metres, and each lamp's width, height and stand-off. */
export const PULL_GAUGE_PITCH = 0.011
export const PULL_GAUGE_WIDTH = 0.008
export const PULL_GAUGE_HEIGHT = 0.006
export const PULL_GAUGE_DEPTH = 0.003
/** How far under the rod's hole the row sits, metres. */
export const PULL_GAUGE_BELOW = 0.03
/** How far in from the cabinet's side the row keeps, metres. */
export const PULL_GAUGE_SIDE_CLEAR = 0.02

/**
 * The lamps' centres, lowest pull first (left to right as the player faces the machine): on the
 * front face, `PULL_GAUGE_BELOW` under where the rod goes through it, centred on the rod — slid
 * in from the side when the rod is close enough to it that the row would run off the cabinet.
 */
export function pullGaugeOf(controls: CabinetControls): ControlPoint[] {
  const { at, axis } = controls.plunger
  const frontZ = at.z - PLUNGER_KNOB_OUT
  const toFront = axis.z > 0 ? PLUNGER_KNOB_OUT / axis.z : 0
  const y = at.y - axis.y * toFront - PULL_GAUGE_BELOW
  const half = ((PULL_GAUGE_SEGMENTS - 1) * PULL_GAUGE_PITCH) / 2
  const limit = controls.body.halfWidth - PULL_GAUGE_SIDE_CLEAR - PULL_GAUGE_WIDTH / 2 - half
  const centre = Math.max(-limit, Math.min(limit, at.x))
  const lamps: ControlPoint[] = []
  for (let i = 0; i < PULL_GAUGE_SEGMENTS; i++) {
    lamps.push({ x: centre - half + i * PULL_GAUGE_PITCH, y, z: frontZ + PULL_GAUGE_DEPTH / 2 })
  }
  return lamps
}

/** How much of the pull the rod is at, 0…1, from how far it is drawn. */
export function plungerTension(drawn: number): number {
  return Math.max(0, Math.min(1, drawn / PLUNGER_TRAVEL))
}

/** How many lamps a pull lights: none at rest, one for any pull at all, every one fully drawn. */
export function gaugeLit(tension: number, segments: number = PULL_GAUGE_SEGMENTS): number {
  if (!(tension > 0)) return 0
  return Math.min(segments, Math.max(1, Math.ceil(tension * segments - 1e-9)))
}

/** What the gauge shows this frame: lamps lit, and the last shot's lamp (−1 before the first). */
export interface GaugeReading {
  readonly lit: number
  readonly marker: number
}

/**
 * The gauge over time. It lights with the pull; when the rod goes home the deepest lamp that pull
 * reached becomes the marker, and the marker stays through the next pull — the thing to match.
 */
export class PullGauge {
  private deepest = 0
  private marked = -1

  update(tension: number): GaugeReading {
    const lit = gaugeLit(tension)
    if (lit > 0) {
      this.deepest = Math.max(this.deepest, lit)
    } else if (this.deepest > 0) {
      this.marked = this.deepest - 1
      this.deepest = 0
    }
    return { lit, marker: this.marked }
  }
}

/** The gauge's lamps: green for the soft half, amber to 85%, red at the top; dark when off. */
export const GAUGE_OFF = 0x1c261c
export const GAUGE_SOFT = 0x39ff6b
export const GAUGE_FIRM = 0xffc23a
export const GAUGE_HARD = 0xff4a2a
/** The last shot's lamp, when it is not lit by this pull. */
export const GAUGE_MARKER = 0xf4fff6

/** Lamp `index`'s colour when lit. */
export function gaugeColour(index: number, segments: number = PULL_GAUGE_SEGMENTS): number {
  const share = (index + 1) / segments
  return share <= 0.5 ? GAUGE_SOFT : share <= 0.85 ? GAUGE_FIRM : GAUGE_HARD
}

/** The knob's light over the grab zone, and at a full pull: it warms from one to the other. */
export const KNOB_COOL = 0x39ff6b
export const KNOB_HOT = 0xff5a1f

/** Two 0xRRGGBB colours mixed channel by channel, `t` of the way from `a` to `b`, rounded. */
export function mixHex(a: number, b: number, t: number): number {
  const k = Math.max(0, Math.min(1, t))
  let out = 0
  for (const shift of [16, 8, 0]) {
    const from = (a >> shift) & 0xff
    const to = (b >> shift) & 0xff
    out |= Math.round(from + (to - from) * k) << shift
  }
  return out
}

/** The knob's colour for a pull: `KNOB_COOL` at rest, `KNOB_HOT` fully drawn. */
export function knobColour(tension: number): number {
  return mixHex(KNOB_COOL, KNOB_HOT, tension)
}

/**
 * Whether a point is close enough to a control to be reaching for it rather than for the cabinet
 * itself.
 *
 * **Here because two callers need the same answer and used to have one between them.**
 * `cabinetGrab` has asked this since M7-13 — the cost of the two mistakes is not symmetric — and
 * `CabinetRig.advanceShove` never asked it at all, so a hand arriving at a flipper button fast
 * enough to press it also shoved the cabinet. Measured on the Quest on 31 Aug 2026, build 52:
 * 79 nudges in a fifteen-minute session, 57% of them within 250 ms of a flipper press, median gap
 * 112 ms. Reported as the machine moving on its own and the game glitching.
 *
 * Generously sized — the plunger's own grab radius, and a button's reach doubled. A nudge refused
 * near a button costs a player nothing, because the rest of the cabinet still nudges; a nudge
 * delivered by a flipper press costs them the ball.
 */
export function onAControl(controls: CabinetControls, x: number, y: number, z: number): boolean {
  if (inPlungerZone(controls.plunger, x, y, z)) return true
  for (const id of PRESSABLE_IDS) {
    const control = controls[id]
    if (near(x, y, z, control.at, control.radius * 2)) return true
  }
  return false
}

function near(x: number, y: number, z: number, centre: ControlPoint, radius: number): boolean {
  const dx = x - centre.x
  const dy = y - centre.y
  const dz = z - centre.z
  return dx * dx + dy * dy + dz * dz <= radius * radius
}

/**
 * Distance from a control's *axis*, metres — how far off the button's face a point is, ignoring
 * how deep it has gone (M8-28).
 *
 * **This is what "the capsule around it" was always supposed to mean.** `distanceTo` is a sphere,
 * so depth and lateral offset traded against each other: with a 30 mm radius and an 8 mm press
 * threshold the usable band ran 22 mm deep at the centre and 2.8 mm at 28 mm off-centre — the
 * button shrank as you pressed it — and a fingertip that carried on past 30 mm left the sphere and
 * released the button it was pressing.
 *
 * Measured on the Quest, build 51, 31 Aug 2026: 274 flipper presses, median hold 41 ms, and 24% of
 * them under 15 ms — one frame at 72 Hz, against a 25 ms coil ramp. Those were not presses; they
 * were a fingertip crossing the shell on its way through.
 */
export function offAxis(control: PressableControl, x: number, y: number, z: number): number {
  const dx = x - control.at.x
  const dy = y - control.at.y
  const dz = z - control.at.z
  const along = dx * control.inward.x + dy * control.inward.y + dz * control.inward.z
  const px = dx - along * control.inward.x
  const py = dy - along * control.inward.y
  const pz = dz - along * control.inward.z
  return Math.sqrt(px * px + py * py + pz * pz)
}

// `distanceTo` — the straight-line distance to a control's centre — lived here and is gone
// (M8-28). It was the sphere test `offAxis` above replaces, its last caller went with the capsule,
// and `check:reachable` said so on the same build. Kept in the history rather than in the file: a
// geometry helper nothing calls is the next person's obvious way to write the bug again.

/**
 * How far past the button's face a point has travelled, metres. Negative means outside it.
 *
 * The projection onto `inward` alone, deliberately: a finger that slides *along* the rail has
 * travelled a long way and pressed nothing, and a press measured as total displacement would
 * fire on it.
 */
export function inwardTravel(
  control: PressableControl,
  x: number,
  y: number,
  z: number,
): number {
  const dx = x - control.at.x
  const dy = y - control.at.y
  const dz = z - control.at.z
  return dx * control.inward.x + dy * control.inward.y + dz * control.inward.z
}
